import type { SegmentsOption } from '@openx/types/targeting/customVariables.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ValidationResult } from '../../../shared/BulkAdd/types.ts';
import { useGetContextualData } from '../../hooks/useGetContextualData.ts';
import { parseBulkContextualSegment, validateBulkContextualSegments } from '../../utils/bulkAdd.ts';
import {
  buildProviderNamesMap,
  buildProviderScopedSegmentsMap,
  type ContextualInclusionType,
  type EnrichedProviderSegments,
  getContextualSegmentKey,
  normalizeProviderId,
  removeEnrichedSegment,
  resolveContextualProviderId,
  setEnrichedSegmentInclusion,
} from '../../utils/enrichment.ts';
import {
  buildContextualMisconfigurationContext,
  isContextualSegmentMisconfigured,
} from '../../utils/misconfiguration.ts';
import type { SelectedSegmentWithMode } from '../components/SelectedContextualSegmentItem.tsx';
import { useContextualSegmentFilters } from './useContextualSegmentFilters.ts';

type ContextualSegmentsDrawerAdapterProps = {
  enrichedSegments: EnrichedProviderSegments[];
  accountUid: string;
  onClose: () => void;
  onApply: (data: EnrichedProviderSegments[]) => void;
};

const getSelectionMembership = (selections: EnrichedProviderSegments[]): Set<string> => {
  const membership = new Set<string>();
  for (const selection of selections) {
    for (const segment of selection.includes) {
      membership.add(
        `${getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id })}:includes`
      );
    }
    for (const segment of selection.excludes) {
      membership.add(
        `${getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id })}:excludes`
      );
    }
  }
  return membership;
};

const areSelectionsEqual = (
  firstSelections: EnrichedProviderSegments[],
  secondSelections: EnrichedProviderSegments[]
) => {
  const firstMembership = getSelectionMembership(firstSelections);
  const secondMembership = getSelectionMembership(secondSelections);
  if (firstMembership.size !== secondMembership.size) return false;
  return Array.from(firstMembership).every(value => secondMembership.has(value));
};

const getSelectedSegmentIdsFromEnriched = (selections: EnrichedProviderSegments[]): string[] => {
  const ids = new Set<string>();
  for (const selection of selections) {
    for (const segment of selection.includes) {
      ids.add(segment.id);
    }
    for (const segment of selection.excludes) {
      ids.add(segment.id);
    }
  }
  return Array.from(ids);
};

export const useContextualSegmentsDrawerAdapter = ({
  enrichedSegments,
  accountUid,
  onClose,
  onApply,
}: ContextualSegmentsDrawerAdapterProps) => {
  const { t } = useTranslation();
  const segmentFilters = useContextualSegmentFilters();
  const selectedSegmentIds = useMemo(() => getSelectedSegmentIdsFromEnriched(enrichedSegments), [enrichedSegments]);
  const {
    segments,
    allSegments,
    selectedSegments,
    loading,
    misconfigurationLoading,
    providerIds,
    providerName,
    dataPartnerOptions,
    dataPartnerOptionsLoading,
    hasOwnSegments,
    totalCount,
    loadingMore,
    hasMore,
    handleLoadMore,
  } = useGetContextualData(accountUid, selectedSegmentIds, segmentFilters);

  const providerNamesMap = useMemo(
    () => buildProviderNamesMap(allSegments ?? segments ?? [], providerIds, providerName),
    [allSegments, segments, providerIds, providerName]
  );
  // Built from the selected-segments lookup only, so search/filter requests that replace the list page
  // neither hide the warning while in flight nor change the verdict once they resolve.
  const misconfigurationContext = useMemo(
    () => buildContextualMisconfigurationContext(selectedSegments, providerIds),
    [selectedSegments, providerIds]
  );
  // Only segments persisted before the drawer opened can be misconfigured - anything picked here comes
  // straight from the account-scoped list, and is not covered by the selected-segments lookup.
  const persistedSegmentKeys = useMemo(
    () =>
      new Set(
        enrichedSegments.flatMap(selection =>
          [...selection.includes, ...selection.excludes].map(segment =>
            getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id })
          )
        )
      ),
    [enrichedSegments]
  );

  const initialSelections = enrichedSegments;
  const [draftSelections, setDraftSelections] = useState<EnrichedProviderSegments[]>(initialSelections);

  useEffect(() => {
    setDraftSelections(initialSelections);
  }, [initialSelections]);

  const segmentsMap = useMemo(() => {
    return buildProviderScopedSegmentsMap(allSegments ?? segments, providerIds, providerNamesMap);
  }, [allSegments, providerIds, segments, providerNamesMap]);

  // Build selectedIds from enriched segments
  const selectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const selection of draftSelections) {
      for (const segment of selection.includes) {
        ids.add(getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id }));
      }
      for (const segment of selection.excludes) {
        ids.add(getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id }));
      }
    }
    return ids;
  }, [draftSelections]);

  // Build selectedIdsMap from enriched segments
  const selectedIdsMap = useMemo(() => {
    const map = new Map<string, 'includes' | 'excludes'>();
    for (const selection of draftSelections) {
      for (const segment of selection.includes) {
        map.set(getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id }), 'includes');
      }
      for (const segment of selection.excludes) {
        map.set(getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id }), 'excludes');
      }
    }
    return map;
  }, [draftSelections]);

  const toSelectedSegment = useCallback(
    (
      selection: EnrichedProviderSegments,
      segment: SegmentsOption,
      inclusionType: ContextualInclusionType
    ): SelectedSegmentWithMode => {
      const isMisconfigured =
        !misconfigurationLoading &&
        persistedSegmentKeys.has(
          getContextualSegmentKey({ providerId: selection.providerId, segmentId: segment.id })
        ) &&
        isContextualSegmentMisconfigured(
          { providerId: selection.providerId, segmentId: segment.id },
          misconfigurationContext
        );

      return {
        providerId: selection.providerId,
        // Use segment directly from enriched state with provider override
        segment: {
          ...segment,
          provider_name: providerNamesMap[selection.providerId] || selection.providerId,
          contextual_provider: selection.providerId,
        },
        inclusionType,
        ...(isMisconfigured && { isMisconfigured }),
      };
    },
    [misconfigurationLoading, misconfigurationContext, persistedSegmentKeys, providerNamesMap]
  );

  const selectedIncludes = useMemo(
    (): SelectedSegmentWithMode[] =>
      draftSelections.flatMap(selection =>
        selection.includes.map(segment => toSelectedSegment(selection, segment, 'includes'))
      ),
    [draftSelections, toSelectedSegment]
  );

  const selectedExcludes = useMemo(
    (): SelectedSegmentWithMode[] =>
      draftSelections.flatMap(selection =>
        selection.excludes.map(segment => toSelectedSegment(selection, segment, 'excludes'))
      ),
    [draftSelections, toSelectedSegment]
  );

  const getAvailableSegmentKey = useCallback(
    (segment: SegmentsOption) => {
      const providerId = resolveContextualProviderId(segment, providerIds) ?? '';
      return getContextualSegmentKey({ providerId, segmentId: segment.id });
    },
    [providerIds]
  );
  const isSegmentProviderResolved = useCallback(
    (segment: SegmentsOption) => Boolean(resolveContextualProviderId(segment, providerIds)),
    [providerIds]
  );

  const setItemInclusion = useCallback(
    (segment: SegmentsOption, inclusionType: 'includes' | 'excludes') => {
      const providerId = resolveContextualProviderId(segment, providerIds);
      if (!providerId) return;
      setDraftSelections(previous => setEnrichedSegmentInclusion(previous, segment, inclusionType, providerId));
    },
    [providerIds]
  );

  const onItemInclude = useCallback(
    (segment: SegmentsOption) => setItemInclusion(segment, 'includes'),
    [setItemInclusion]
  );

  const onItemExclude = useCallback(
    (segment: SegmentsOption) => setItemInclusion(segment, 'excludes'),
    [setItemInclusion]
  );

  const onItemRemove = useCallback((item: SelectedSegmentWithMode) => {
    setDraftSelections(previous => removeEnrichedSegment(previous, item.segment.id, item.providerId));
  }, []);
  const onRemoveAll = useCallback(() => {
    setDraftSelections([]);
  }, []);
  const hasChanges = useMemo(
    () => !areSelectionsEqual(draftSelections, initialSelections),
    [draftSelections, initialSelections]
  );
  const handleApply = useCallback(() => {
    onApply(draftSelections);
  }, [draftSelections, onApply]);
  const handleDiscard = useCallback(() => {
    setDraftSelections(initialSelections);
    onClose();
  }, [initialSelections, onClose]);
  const hasSingleAccountProvider = new Set(providerIds.map(normalizeProviderId).filter(Boolean)).size === 1;
  const isBulkAddDisabled = loading;

  const validateBulkSegments = useCallback(
    // biome-ignore lint/suspicious/useAwait: async required by BulkAddDrawer validate API
    async (segmentIds: string[], inclusionType: ContextualInclusionType): Promise<ValidationResult> => {
      const catalogSegments = allSegments ?? segments ?? [];

      const result = validateBulkContextualSegments(
        segmentIds,
        catalogSegments,
        providerIds,
        selectedIdsMap,
        inclusionType,
        t
      );

      return { errors: result.errors, validItems: result.validItems };
    },
    [allSegments, segments, providerIds, selectedIdsMap, t]
  );

  const addBulkSegments = useCallback(
    (serializedSegments: string[], inclusionType: ContextualInclusionType) => {
      // Parse and enrich ALL segments first
      const enrichedSegmentsToAdd: Array<{ segment: SegmentsOption; providerId: string }> = [];

      serializedSegments.forEach(serialized => {
        const parsed = parseBulkContextualSegment(serialized);
        // NOTE: Parse failures cause silent drops. This should not happen since validation
        // already parsed these successfully, but if getContextualSegmentKey format changes
        // without updating parseBulkContextualSegment, items will be silently lost here.
        if (!parsed) return;

        // Use the same provider-scoped segmentsMap that's already built for the drawer
        // This ensures consistent catalog lookup logic
        const lookupKey = getContextualSegmentKey({ providerId: parsed.providerId, segmentId: parsed.segmentId });
        const catalogMetadata = segmentsMap[lookupKey];

        const providerId = parsed.providerId;
        const fallbackProviderName = providerNamesMap[providerId] || providerId;

        // Build full segment object with metadata from catalog
        const enrichedSegment: SegmentsOption = catalogMetadata
          ? {
              ...catalogMetadata,
              provider_name: fallbackProviderName,
              contextual_provider: providerId,
            }
          : {
              id: parsed.segmentId,
              name: parsed.segmentId,
              provider_name: fallbackProviderName,
              contextual_provider: providerId,
            };

        enrichedSegmentsToAdd.push({ segment: enrichedSegment, providerId });
      });

      // Single state update to add all enriched segments at once
      setDraftSelections(previous => {
        let updated = previous;
        enrichedSegmentsToAdd.forEach(({ segment, providerId }) => {
          updated = setEnrichedSegmentInclusion(updated, segment, inclusionType, providerId);
        });
        return updated;
      });
    },
    [segmentsMap, providerNamesMap]
  );

  return {
    availableSegments: segments,
    totalCount,
    selectedIds,
    selectedIdsMap,
    getAvailableSegmentKey,
    isSegmentProviderResolved,
    onItemInclude,
    onItemExclude,
    pagination: { hasMore, isLoading: loadingMore, onLoadMore: handleLoadMore },
    selectedIncludes,
    selectedExcludes,
    onItemRemove,
    onRemoveAll,
    loading,
    handleApply,
    handleDiscard,
    hasChanges,
    hasSingleAccountProvider,
    hasOwnSegments,
    isBulkAddDisabled,
    validateBulkSegments,
    addBulkSegments,
    segmentFilters,
    dataPartnerOptions,
    dataPartnerOptionsLoading,
  };
};
